import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  Flex,
  Text,
  Button,
  Radio,
  RadioGroup,
  Badge,
  Tag,
  Wrap,
  WrapItem,
  Spinner,
  Center,
  Divider,
  useToast,
} from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { mergeMovements } from '../../api/endpointsBackend';
import { formatMoney } from '../../utils/formatters';

/**
 * Compact movement row used in the selection and preview lists
 */
const MovementRow = ({ movement, highlight = false }) => {
  const { t } = useTranslation('movements');
  const isTransfer = movement.type === 'Transfer';
  const accountText = isTransfer
    ? `${movement.from_account?.name || '?'} → ${movement.to_account?.name || '?'}`
    : movement.account?.name || t('fallback.unknown');

  const tagNames = (movement.tag_movements || []).map(tm => tm.tag?.name).filter(Boolean);

  return (
    <Flex
      py={2}
      px={3}
      borderLeft="3px solid"
      borderLeftColor={highlight ? 'redesign.accent.green' : 'redesign.accent.blue'}
      bg={highlight ? 'rgba(34, 197, 94, 0.10)' : 'redesign.bg.card'}
      align="center"
      mb="1px"
      borderRadius="sm"
    >
      <Box flex={1} minW={0}>
        <Flex align="center" gap={1}>
          <Text fontSize="xs" fontWeight="600" noOfLines={1}>
            {movement.note || movement.description || t('fallback.noNote')}
          </Text>
          {highlight && (
            <Badge colorScheme="green" fontSize="2xs" variant="subtle">
              {t('modals.merge.primaryBadge')}
            </Badge>
          )}
        </Flex>
        <Flex align="center" gap={2}>
          <Text fontSize="2xs" color="redesign.text.muted" noOfLines={1}>
            {accountText}
          </Text>
          <Text fontSize="2xs" color="redesign.text.muted">
            {movement.date}
          </Text>
        </Flex>
        {tagNames.length > 0 && (
          <Wrap spacing={1} mt={1}>
            {tagNames.map((name, idx) => (
              <WrapItem key={idx}>
                <Tag size="sm" colorScheme="blue" variant="subtle" fontSize="2xs">
                  {name}
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        )}
      </Box>
      <Text
        fontSize="xs"
        fontWeight="600"
        ml={2}
        flexShrink={0}
        color={
          movement.type === 'Income' ? 'redesign.accent.green' :
          movement.type === 'Expenses' ? 'redesign.accent.red' :
          'redesign.text'
        }
      >
        {formatMoney(movement.amount, movement.currency)}
      </Text>
    </Flex>
  );
};

/**
 * Warning banner component
 */
const WarningBanner = ({ warnings, resulting }) => {
  const { t } = useTranslation('movements');
  if (!warnings || warnings.length === 0) return null;

  return (
    <Box mb={3}>
      {warnings.map((w, idx) => (
        <Flex
          key={idx}
          align="flex-start"
          gap={2}
          bg={w.blocking ? 'rgba(239, 68, 68, 0.10)' : 'rgba(239, 117, 71, 0.10)'}
          px={3}
          py={2}
          borderRadius="md"
          mb={1}
        >
          <WarningIcon
            color={w.blocking ? 'redesign.accent.red' : 'redesign.accent.expense'}
            boxSize={3.5}
            mt={0.5}
            flexShrink={0}
          />
          <Text
            fontSize="xs"
            color={w.blocking ? 'redesign.accent.red' : 'redesign.accent.expense'}
          >
            {/* Localized copy per warning code; codes without a key keep the
                backend message. */}
            {t(`modals.merge.warnings.${w.code}`, {
              defaultValue: w.message,
              files: (w.files || []).join(', '),
              from: resulting?.from_account?.name,
              to: resulting?.to_account?.name,
              amount: resulting && formatMoney(resulting.amount, resulting.currency),
              exchange: resulting?.currency_exchange && formatMoney(
                resulting.currency_exchange.value_target,
                resulting.currency_exchange.currency_target,
              ),
            })}
          </Text>
        </Flex>
      ))}
    </Box>
  );
};

/**
 * One view: the movements to choose from, and under them what keeping the
 * chosen one would do (the dry-run preview). The preview is fetched when the
 * dialog opens and again whenever the kept movement changes.
 */
export default function MergeMovementsModal({ isOpen, onClose, movements, onMergeComplete }) {
  const toast = useToast();
  const { t } = useTranslation('movements');
  const [primaryId, setPrimaryId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isMerging, setIsMerging] = useState(false);

  // Set default primary to first movement
  const effectivePrimaryId = primaryId || (movements.length > 0 ? movements[0].id : null);

  const movementIds = useMemo(() => movements.map(m => m.id), [movements]);
  // The parent hands over a new array on every render; the ids are what count.
  const movementIdsKey = movementIds.join(',');

  useEffect(() => {
    if (!isOpen || !effectivePrimaryId) return undefined;
    // A response for a movement that is no longer the kept one is dropped.
    let isStale = false;
    setIsLoadingPreview(true);
    setPreviewError(null);
    mergeMovements(effectivePrimaryId, movementIds, true)
      .then((result) => {
        if (isStale) return;
        setPreview(result);
        setIsLoadingPreview(false);
      })
      .catch((error) => {
        if (isStale) return;
        setPreview(null);
        setPreviewError({ detail: error.response?.data?.detail });
        setIsLoadingPreview(false);
      });
    return () => { isStale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, effectivePrimaryId, movementIdsKey]);

  const handleChoose = (id) => {
    if (!isMerging) setPrimaryId(id);
  };

  const handleConfirmMerge = async () => {
    if (!effectivePrimaryId) return;
    setIsMerging(true);
    try {
      await mergeMovements(effectivePrimaryId, movementIds, false);
      toast({
        title: t('modals.merge.toasts.successTitle'),
        description: t('modals.merge.toasts.successDescription', { count: movements.length - 1 }),
        status: 'success',
        duration: 3000,
      });
      const primaryMovement = movements.find(m => m.id === effectivePrimaryId);
      handleClose();
      onMergeComplete?.(effectivePrimaryId, primaryMovement?.date);
    } catch (error) {
      toast({
        title: t('modals.merge.toasts.errorTitle'),
        description: error.response?.data?.detail || t('modals.merge.toasts.mergeError'),
        status: 'error',
        duration: 4000,
      });
    } finally {
      setIsMerging(false);
    }
  };

  const handleClose = () => {
    setPrimaryId(null);
    setPreview(null);
    setPreviewError(null);
    setIsLoadingPreview(false);
    setIsMerging(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md" pb={2}>
          {t('modals.merge.title')}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {/* Select the movement to keep */}
          <Text fontSize="sm" color="redesign.text" mb={3}>
            {t('modals.merge.selectPrimary')}
          </Text>

          <RadioGroup
            value={String(effectivePrimaryId)}
            onChange={(val) => handleChoose(Number(val))}
            isDisabled={isMerging}
          >
            <Box maxH="240px" overflowY="auto">
              {movements.map((mov) => (
                <Flex
                  key={mov.id}
                  align="center"
                  gap={2}
                  mb={1}
                  cursor="pointer"
                  onClick={() => handleChoose(mov.id)}
                >
                  <Radio value={String(mov.id)} size="sm" flexShrink={0} />
                  <Box flex={1}>
                    <MovementRow
                      movement={mov}
                      highlight={mov.id === effectivePrimaryId}
                    />
                  </Box>
                </Flex>
              ))}
            </Box>
          </RadioGroup>

          <Divider my={3} />

          {/* What keeping the chosen movement would do */}
          {!preview && isLoadingPreview && (
            <Center py={8}>
              <Spinner size="md" color="redesign.text.muted" />
            </Center>
          )}

          {previewError && (
            <Flex align="center" gap={2} bg="rgba(239, 68, 68, 0.10)" px={3} py={2} borderRadius="md">
              <WarningIcon color="redesign.accent.red" boxSize={4} flexShrink={0} />
              <Text fontSize="sm" color="redesign.accent.red" fontWeight="500">
                {previewError.detail || t('modals.merge.toasts.previewError')}
              </Text>
            </Flex>
          )}

          {preview && (
            // Stays on screen, dimmed, while the next preview loads, so the
            // dialog does not jump when the kept movement changes.
            <Box opacity={isLoadingPreview ? 0.5 : 1} transition="opacity 0.15s">
              {/* Warnings */}
              <WarningBanner warnings={preview.warnings} resulting={preview.resulting_movement} />

              {/* Resulting movement preview */}
              <Text fontSize="sm" fontWeight="600" mb={2}>
                {t('modals.merge.resultingMovement')}
              </Text>
              <MovementRow movement={preview.resulting_movement} highlight />

              {/* Impact summary */}
              {(preview.tags_to_add > 0 || preview.transactions_to_transfer > 0 || preview.groups_to_transfer > 0) && (
                <Flex gap={2} mt={2} flexWrap="wrap">
                  {preview.tags_to_add > 0 && (
                    <Badge colorScheme="blue" variant="subtle" fontSize="2xs">
                      {t(preview.tags_to_add === 1 ? 'modals.merge.impacts.tagsOne' : 'modals.merge.impacts.tagsOther', { count: preview.tags_to_add })}
                    </Badge>
                  )}
                  {preview.transactions_to_transfer > 0 && (
                    <Badge colorScheme="green" variant="subtle" fontSize="2xs">
                      {t(preview.transactions_to_transfer === 1 ? 'modals.merge.impacts.transactionsOne' : 'modals.merge.impacts.transactionsOther', { count: preview.transactions_to_transfer })}
                    </Badge>
                  )}
                  {preview.groups_to_transfer > 0 && (
                    <Badge colorScheme="purple" variant="subtle" fontSize="2xs">
                      {t('modals.merge.impacts.group', { count: preview.groups_to_transfer })}
                    </Badge>
                  )}
                </Flex>
              )}

              {/* Irreversible warning */}
              <Flex align="center" gap={2} bg="rgba(239, 68, 68, 0.10)" px={3} py={2} borderRadius="md" mt={3}>
                <WarningIcon color="redesign.accent.red" boxSize={4} flexShrink={0} />
                <Text fontSize="sm" color="redesign.accent.red" fontWeight="500">
                  {t('modals.merge.warning')}
                </Text>
              </Flex>
            </Box>
          )}
        </ModalBody>

        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={handleClose} isDisabled={isMerging}>
            {t('modals.merge.buttons.cancel')}
          </Button>
          <Button
            size="sm"
            colorScheme="red"
            onClick={handleConfirmMerge}
            isLoading={isMerging}
            isDisabled={isLoadingPreview || !preview?.can_merge}
          >
            {t('modals.merge.buttons.confirm')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
